import { z } from "zod";

/**
 * Restaurant slug rules from docs/architecture/storefront-sites.md.
 * Used by the API, the storefront and the Caddy ask hook.
 */

export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
	"www",
	"app",
	"admin",
	"api",
	"cdn",
	"files",
	"static",
	"assets",
	"img",
	"clerk",
	"accounts",
	"auth",
	"login",
	"mail",
	"email",
	"smtp",
	"ftp",
	"ns1",
	"ns2",
	"status",
	"docs",
	"help",
	"support",
	"blog",
	"test",
	"staging",
	"dev",
	"pay",
	"billing",
	"luckymenu",
	"lucky",
]);

const SLUG_FORMAT = /^[a-z0-9](?:[a-z0-9-]{1,38})[a-z0-9]$/;

export function isReservedSlug(slug: string): boolean {
	const s = slug.toLowerCase();
	return RESERVED_SLUGS.has(s) || s.startsWith("_") || s.startsWith("xn--");
}

export type SlugProblem = "format" | "reserved";

export function checkSlug(slug: string): SlugProblem | null {
	if (isReservedSlug(slug)) return "reserved";
	if (!SLUG_FORMAT.test(slug)) return "format";
	return null;
}

export function isValidSlug(slug: string): boolean {
	return checkSlug(slug) === null;
}

export const slugSchema = z.string().refine(isValidSlug, {
	message: "3 to 40 lowercase letters, digits or hyphens, not a reserved name",
});

/** Lower-cases a host and removes the port and any trailing dot. */
export function normaliseHost(host: string): string {
	let h = host.trim().toLowerCase();
	if (h.startsWith("[")) return h; // IPv6 literal, never a restaurant
	const colon = h.lastIndexOf(":");
	if (colon !== -1) h = h.slice(0, colon);
	if (h.endsWith(".")) h = h.slice(0, -1);
	return h;
}

/**
 * The restaurant slug for `<slug>.<rootDomain>`, or null when the host is not
 * a single label under the root, or the label is not a valid slug.
 */
export function slugFromHost(host: string, rootDomain: string): string | null {
	const h = normaliseHost(host);
	const suffix = `.${normaliseHost(rootDomain)}`;
	if (!h.endsWith(suffix)) return null;
	const label = h.slice(0, -suffix.length);
	if (label.length === 0 || label.includes(".")) return null;
	return isValidSlug(label) ? label : null;
}

/**
 * A slug suggested from a restaurant name: "Spice Garden & Café" → "spice-garden-cafe".
 * Letters outside a to z are dropped after removing accents. The result may still
 * be too short or reserved; check it with `checkSlug`.
 */
export function suggestSlug(name: string): string {
	return name
		.normalize("NFKD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase()
		.replace(/&/g, " and ")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 40)
		.replace(/-+$/, "");
}
